const asFiniteMilliseconds = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number) : null;
};

const parseTimestamp = (value) => {
  const timestamp = Date.parse(String(value || ''));
  return Number.isFinite(timestamp) ? timestamp : null;
};

export function getGenerationDurationMs(campaign) {
  if (!campaign || typeof campaign !== 'object') return null;

  const direct = asFiniteMilliseconds(campaign.generation_time_ms);
  if (direct != null) return direct;

  const rawRequestSeconds = campaign.performance_metrics?.total_request_seconds;
  const requestSeconds = Number(rawRequestSeconds);
  if (rawRequestSeconds != null && rawRequestSeconds !== '' && Number.isFinite(requestSeconds) && requestSeconds >= 0) {
    return Math.round(requestSeconds * 1000);
  }

  const startedAt = parseTimestamp(campaign.generation_started_at);
  const completedAt = parseTimestamp(campaign.generation_completed_at);
  if (startedAt != null && completedAt != null) {
    return Math.max(0, completedAt - startedAt);
  }

  // Backward compatibility for campaigns generated before total request timing
  // was stored. This metric excludes queue time, but is the best historic value.
  const rawLegacySeconds = campaign.performance_metrics?.total;
  const legacySeconds = Number(rawLegacySeconds);
  if (rawLegacySeconds != null && rawLegacySeconds !== '' && Number.isFinite(legacySeconds) && legacySeconds >= 0) {
    return Math.round(legacySeconds * 1000);
  }
  return null;
}

export function formatGenerationDurationMs(milliseconds) {
  const value = asFiniteMilliseconds(milliseconds);
  if (value == null) return '';
  const totalSeconds = Math.max(0, Math.round(value / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function attachGenerationTiming(campaign, startedAtMs, completedAtMs = Date.now()) {
  if (!campaign || typeof campaign !== 'object') return campaign;

  const explicitStart = asFiniteMilliseconds(startedAtMs);
  const storedStart = parseTimestamp(campaign.generation_started_at);
  const start = explicitStart ?? storedStart;
  const completed = asFiniteMilliseconds(completedAtMs) ?? Date.now();
  const existingDuration = getGenerationDurationMs(campaign);
  const duration = start != null
    ? Math.max(0, completed - start)
    : existingDuration;

  if (duration == null) return campaign;
  const performanceMetrics = {
    ...(campaign.performance_metrics && typeof campaign.performance_metrics === 'object'
      ? campaign.performance_metrics
      : {}),
    total_request_seconds: Number((duration / 1000).toFixed(3)),
  };

  return {
    ...campaign,
    generation_started_at: start != null
      ? new Date(start).toISOString()
      : campaign.generation_started_at,
    generation_completed_at: start != null
      ? new Date(completed).toISOString()
      : campaign.generation_completed_at,
    generation_time_ms: duration,
    performance_metrics: performanceMetrics,
  };
}
